import type { CandidateRow } from '@shared/models/element-import.model';
import type { Baseline } from '@shared/models/project.model';
import type { AiImportMappingUnsupportedReason } from '@shared/models/ai-import-mapping.model';
import { isRowDiscarded } from '@shared/helpers/candidate-discard.helpers';
import type { ImportedElementsByRowId } from './candidate-status.helpers';
import { resolveBulkActionTargetIds } from './list-multi-select.helpers';

export const AI_IMPORT_MAPPING_TOOLTIP = 'Suggest mapping with AI';

export const AI_IMPORT_MAPPING_UNSUPPORTED_LABEL: Record<
  AiImportMappingUnsupportedReason,
  string
> = {
  transport: 'Transport — map this another way',
  price_addon: 'Price add-on — map this another way',
  construction: 'Whole construction — not a material product',
  insufficient_evidence: 'Not enough evidence for a product match',
  other: 'Cannot map this resource as a generic product',
};

export const AI_IMPORT_MAPPING_BUSY_TOOLTIP = 'Mapping with AI';

export const aiImportMappingTooltip = (
  targetCount: number,
  options?: { busy?: boolean },
): string => {
  if (options?.busy) return AI_IMPORT_MAPPING_BUSY_TOOLTIP;
  return targetCount > 1
    ? `${AI_IMPORT_MAPPING_TOOLTIP} for ${targetCount} rows`
    : AI_IMPORT_MAPPING_TOOLTIP;
};

export const aiImportMappingMenuLabel = (targetCount: number): string =>
  targetCount > 1
    ? `Suggest mapping with AI (${targetCount})`
    : AI_IMPORT_MAPPING_TOOLTIP;

/** Incomplete, visible rows that an AI mapping action on `sourceRow` should hit. */
export const selectAiImportMappingTargetRows = (
  sourceRow: CandidateRow,
  allRows: readonly CandidateRow[],
  selectedIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
  imported: ImportedElementsByRowId,
  version: Baseline | undefined,
): CandidateRow[] => {
  const targetIds = new Set(
    resolveBulkActionTargetIds(sourceRow.id, selectedIds, visibleRowIds),
  );
  return allRows.filter(
    (row) =>
      targetIds.has(row.id) &&
      !imported.has(row.id) &&
      !isRowDiscarded(version, row.id),
  );
};

export type AiImportMappingBatchKind =
  | 'mapped'
  | 'stale'
  | 'unsupported'
  | 'no_match'
  | 'missing_product'
  | 'no_draft'
  | 'failed';

export type AiImportMappingSnackbar = {
  message: string;
  variant: 'success' | 'info' | 'warning' | 'error';
};

const SINGLE_SNACKBAR: Record<
  AiImportMappingBatchKind,
  AiImportMappingSnackbar
> = {
  mapped: { message: 'Mapped with AI', variant: 'success' },
  stale: { message: 'The row changed. Try again.', variant: 'warning' },
  unsupported: {
    message: AI_IMPORT_MAPPING_UNSUPPORTED_LABEL.other,
    variant: 'info',
  },
  no_match: { message: 'No matching generic product found.', variant: 'info' },
  missing_product: {
    message: 'Suggested product is no longer in the catalogue.',
    variant: 'error',
  },
  no_draft: { message: 'Could not apply the suggestion.', variant: 'error' },
  failed: { message: 'Could not fetch a suggestion.', variant: 'error' },
};

/**
 * Snackbar for an AI mapping run. One row keeps the specific reason or names
 * the mapped product; several rows get a count summary so the UI does not
 * enqueue one toast per target.
 */
export const snackbarForAiImportMappingBatch = (
  kinds: readonly AiImportMappingBatchKind[],
  unsupportedReason?: AiImportMappingUnsupportedReason,
  mappedProductName?: string,
): AiImportMappingSnackbar | undefined => {
  if (kinds.length === 0) return undefined;
  const mapped = kinds.filter((kind) => kind === 'mapped').length;
  if (kinds.length === 1) {
    const [kind] = kinds;
    if (kind === undefined) return undefined;
    if (kind === 'mapped' && mappedProductName) {
      return {
        message: `Mapped to ${mappedProductName}`,
        variant: 'success',
      };
    }
    if (kind === 'unsupported' && unsupportedReason) {
      return {
        message: AI_IMPORT_MAPPING_UNSUPPORTED_LABEL[unsupportedReason],
        variant: 'info',
      };
    }
    return SINGLE_SNACKBAR[kind];
  }
  if (mapped === kinds.length) {
    return {
      message: `${mapped} rows mapped with AI`,
      variant: 'success',
    };
  }
  if (mapped > 0) {
    return {
      message: `Mapped ${mapped} of ${kinds.length} rows`,
      variant: 'warning',
    };
  }
  return { message: 'Could not map the selected rows.', variant: 'error' };
};
